<template>
  <div
    id="draggable"
    ref="modifier"
    class="flex"
    @mousemove="handleMouseMove"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUp"
  >
    <slot />

    <Teleport to="#schedule">
      <transition name="fade">
        <div v-if="active.item" class="w-100 bottom-0 absolute z-70000 left-1/2 transform -translate-x-1/2 m-4 flex flex-col items-stretch space-y-2">
          <div v-if="hasLinkedServices" class="bg-white rounded-xl shadow-lg p-3 text-gray-700 flex items-center justify-between dark:bg-gray-800 dark:text-gray-200">
            <span class="text-sm mr-4">{{ $t('components.schedule.elements.modifier.moveLinkedServices') }}</span>
            <toggle-field v-model="moveLinkedServices" size="xs" />
          </div>
          <div class="bg-red-500 rounded-xl shadow-lg p-3 text-white flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <o-button icon="x" translucent variant="light" @click="handleReset" />
              <span>{{ isReservationActive ? $t('components.schedule.elements.modifier.movingAppointment') : $t('components.schedule.elements.modifier.movingBreak') }}</span>
            </div>
            <o-button outline variant="light" @click="handleConfirm">
              {{ $t('general.confirm') }}
            </o-button>
          </div>
        </div>
      </transition>
    </Teleport>

    <move-reservation-dialog
      ref="moveReservationDialog"
      @success="handleSuccess"
      @cancel="handleCancel"
    />

    <move-break-dialog
      ref="moveBreakDialog"
      @success="handleSuccess"
      @cancel="handleCancel"
    />

    <create-dialog
      ref="createDialog"
      @createBreak="handleCreateBreak"
    />

    <preview-reservation />
  </div>
</template>

<script setup lang="ts">
import MoveReservationDialog from '@/components/schedule/actions/MoveReservation.vue'
import MoveBreakDialog from '@/components/schedule/actions/MoveBreak.vue'

import CreateDialog from '@/components/schedule/actions/Create.vue'

import PreviewReservation from '@/components/schedule/elements/Preview.vue'

import { ToggleField } from '@/components/fields'

import { EmployeeBreak } from '@/_api/models/employeeBreak'
import { Reservation } from '@/_api/models/reservation'

import { ReservationBlock, Timing } from '@/components/schedule/provider/block'

import { useUIStore } from '~/stores/useUIStore'

const { $auth, $date } = useNuxtApp()
const uiStore = useUIStore()

const container = inject('container') as any
const handleCreateBreak = inject('handleCreateBreak') as Function

interface Props {
  sections: any
  provider: any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:active', value: any): void
}>()

const moveReservationDialog = ref<any>()
const moveBreakDialog = ref<any>()
const createDialog = ref<any>()

const active = reactive({
  move: null as any,
  resize: null as any,
  item: null as any,
  hover: null as any,
  variance: null as any,
})

const moveLinkedServices = ref(false)

const position = reactive({ left: 0, top: 0 })
let left = 0
let top = 0
let variance: number | null = null
let originalPositions: Map<string, number> | null = null
let primaryBlock: any = null
let trackedItem: any = null

const minuteSize = computed(() => {
  const value = Number($auth?.info?.minuteSize)
  return Number.isFinite(value) && value > 0 ? value : 2
})
const sectionSize = computed(() => uiStore.schedule.sectionSize)
const isBreakActive = computed(() => active.item instanceof EmployeeBreak)
const isReservationActive = computed(() => active.item instanceof Reservation)
const hasLinkedServices = computed(() => {
  return isReservationActive.value && (active.item?.services?.length || 0) > 1
})

provide('modifier', reactive({
  active,
}))

watch(() => active.item, (val, oldVal) => {
  if (val !== oldVal) {
    if (val) {
      moveLinkedServices.value = false
    } else {
      trackedItem = null
      primaryBlock = null
      originalPositions = null
    }
  }
})

watch(() => active.move, (val, oldVal) => {
  container.scrollable = !val

  if (val && val !== oldVal) {
    if (active.item !== trackedItem) {
      trackedItem = active.item
      originalPositions = null
    }

    primaryBlock = val

    if (!originalPositions) {
      originalPositions = captureOriginalPositions(val)
    }
  }
})

watch(() => active.resize, (val) => {
  container.scrollable = !val
})

watch(() => props.provider, () => {
  handleReset()
})

watch(active, (val) => {
  emit('update:active', val)
}, { deep: true })

watch(moveLinkedServices, (val) => {
  if (val) {
    snapLinkedServices(primaryBlock)
  }
})

function handleMouseMove(event: MouseEvent) {
  if (event.buttons) {
    if (active.move) { moveBlock(event) }
    if (active.resize) { resizeBlock(event) }
  } else {
    active.move = null
    active.resize = null
    active.variance = null
  }
}

function handleMouseDown(event: MouseEvent) {
  left = event.pageX
  top = event.pageY
}

function handleMouseUp(event: MouseEvent) {
  active.move = null
  active.resize = null
  active.variance = null

  if (left === event.pageX && top === event.pageY) {
    handleCreate(event)
  }
}

function handleReset() {
  active.move = null
  active.resize = null
  active.item = null
  active.variance = null
  container.reset()
}

function handleConfirm() {
  if (isReservationActive.value) {
    moveReservationDialog.value?.open(active.item)
  }
  if (isBreakActive.value) {
    moveBreakDialog.value?.open(active.item)
  }
}

function handleSuccess() {
  active.move = null
  active.resize = null
  active.item = null
  active.variance = null
}

function handleCancel() {}

function handleCreate(event: MouseEvent) {
  const isActive = active.move || active.resize || active.item
  const target = event.target as HTMLElement
  const isSlot = target.classList.contains('slot')

  if (!isActive && isSlot) {
    const slotSizeMin = parseInt(target.dataset.slotSize || '15', 10)
    const minuteSizePx = parseFloat(target.dataset.minuteSize || '2')
    const slotHeightPx = slotSizeMin * minuteSizePx
    const slotIndex = slotHeightPx > 0 ? Math.floor(event.offsetY / slotHeightPx) : 0
    const startTime = String(slotIndex * slotSizeMin)

    const offset = calcParentOffset()
    const offsetX = event.pageX - offset.left

    const sections = props.sections
    const sectionKeys = Object.keys(sections)
    const sectionKey = sectionKeys[Math.floor(offsetX / sectionSize.value)]
    const section = sections[sectionKey]

    const employee = section.employee
    const date = $date.parse(section.date.date.value)

    createDialog.value?.open(employee, date, startTime)
  }
}

function moveBlock(event: MouseEvent) {
  const activeMove = active.move
  const offset = calcParentOffset()
  const offsetX = event.pageX - offset.left
  const offsetY = event.pageY - offset.top

  let change = false

  if (!activeMove.isResourceBlock && offsetY > -1) {
    if (!variance || variance < 0) {
      const block = document.getElementById(activeMove.hash)?.getBoundingClientRect()
      const preBuffer = (activeMove?.bufferTimes?.preBuffer || 0) * 2
      variance = event.clientY - (block?.top || 0) - preBuffer
      activeMove.variance = variance
    }

    const start = Math.round((offsetY - (variance || 0)) / minuteSize.value)

    if ((variance || 0) > -1 && start > 0) {
      const previousStart = activeMove.startTime
      activeMove.startTime = start
      change = true

      if (moveLinkedServices.value) {
        shiftLinkedServices(activeMove, start - previousStart)
      }
    }
  }

  const sections = props.sections
  const sectionKeys = Object.keys(sections)
  const sectionKeyFrom = String(activeMove.section || 0)
  const sectionKeyTo = String(sectionKeys[Math.floor(offsetX / sectionSize.value)] || 0)

  const sectionFrom = sections[sectionKeyFrom]
  const sectionTo = sections[sectionKeyTo]

  const isValidMove = () => {
    if (!activeMove.isResourceBlock) {
      return sectionTo.employee.isEmployeeType
    }
    return true
  }

  if (sectionFrom && sectionTo && !activeMove.isTimingFinishing && sectionKeyFrom !== sectionKeyTo && isValidMove()) {
    const blockIndex = sectionFrom?.blocks.findIndex((item: any) => item.hash === activeMove.hash)

    if (sectionKeyTo && blockIndex > -1) {
      sectionFrom.removeBlock(blockIndex)
      sectionTo?.addBlock(activeMove)
      activeMove.section = sectionKeyTo
      activeMove.updateSection(sectionTo)

      if (activeMove.finishingBlock) {
        const finishingBlockIndex = sectionFrom.blocks.findIndex((item: any) => item.hash === activeMove.finishingBlock.hash)
        if (finishingBlockIndex !== -1) {
          sectionFrom.removeBlock(finishingBlockIndex)
          sectionTo.addBlock(activeMove.finishingBlock)
        }
      }
    }

    sectionFrom?.calculateCompression()
    change = true
  }

  if (change) {
    sectionTo?.calculateCompression()
  }

  props.provider.calculateBufferTimes([sectionFrom, sectionTo])
}

function captureOriginalPositions(activeMove: any) {
  if (!(activeMove instanceof ReservationBlock) || !activeMove.reservation) { return null }

  const idReservation = activeMove.reservation.idReservation
  const positions = new Map<string, number>()

  for (const section of Object.values(props.sections) as any[]) {
    for (const block of section.blocks) {
      if (
        !(block instanceof ReservationBlock) ||
        block.isResourceBlock ||
        block.timing !== Timing.duration ||
        block.reservation.idReservation !== idReservation
      ) {
        continue
      }

      positions.set(block.reservationService.idReservationService, block.startTime)
    }
  }

  return positions.size ? positions : null
}

function applyToLinkedServices(activeMove: any, getNewStartTime: (block: any) => number | null | undefined) {
  if (!(activeMove instanceof ReservationBlock) || !activeMove.reservation) { return }

  const idReservation = activeMove.reservation.idReservation
  const idReservationService = activeMove.reservationService.idReservationService
  const shifted = new Set()

  for (const section of Object.values(props.sections) as any[]) {
    for (const block of section.blocks) {
      if (
        !(block instanceof ReservationBlock) ||
        block.isResourceBlock ||
        block.timing !== Timing.duration ||
        block.reservation.idReservation !== idReservation ||
        block.reservationService.idReservationService === idReservationService ||
        shifted.has(block.reservationService.idReservationService)
      ) {
        continue
      }

      const newStartTime = getNewStartTime(block)
      if (newStartTime == null) { continue }

      shifted.add(block.reservationService.idReservationService)
      block.startTime = newStartTime
    }
  }

  if (!shifted.size) { return }

  const affectedSections = new Set<any>()

  for (const section of Object.values(props.sections) as any[]) {
    const hasShiftedBlock = section.blocks.some((block: any) => (
      block instanceof ReservationBlock &&
      block.reservation.idReservation === idReservation &&
      shifted.has(block.reservationService.idReservationService)
    ))

    if (hasShiftedBlock) {
      section.calculateCompression()
      affectedSections.add(section)
    }
  }

  props.provider.calculateBufferTimes([...affectedSections])
}

function shiftLinkedServices(activeMove: any, delta: number) {
  if (!delta) { return }
  applyToLinkedServices(activeMove, block => block.startTime + delta)
}

function snapLinkedServices(activeMove: any) {
  if (!activeMove || !originalPositions || !(activeMove instanceof ReservationBlock) || !activeMove.reservation) { return }

  const originalStart = originalPositions.get(activeMove.reservationService.idReservationService)
  if (originalStart == null) { return }

  const delta = activeMove.startTime - originalStart
  if (!delta) { return }

  const positions = originalPositions

  applyToLinkedServices(activeMove, (block) => {
    const original = positions.get(block.reservationService.idReservationService)
    return original == null ? null : original + delta
  })
}

function resizeBlock(event: MouseEvent) {
  const offset = calcParentOffset()
  const offsetY = event.pageY - offset.top

  if (offsetY > -1) {
    const start = active.resize.startTime
    const finish = Math.round(offsetY / minuteSize.value)
    const duration = finish - start

    if (duration > 5) {
      active.resize.duration = duration
    }

    const section = props.sections[active.resize.section]
    props.sections[active.resize.section]?.calculateCompression()
    props.provider.calculateBufferTimes([section])
  }
}

function scroll(distance: number) {
  if (active?.move) {
    const move = active.move
    const previousStart = move.startTime
    const startTime = previousStart + (distance / minuteSize.value)
    const finishTime = startTime + move.duration

    if (startTime < 0) {
      active.move.startTime = 0
    } else if (finishTime > 1440) {
      active.move.startTime = 1440 - move.duration
    } else {
      active.move.startTime = startTime
    }

    if (moveLinkedServices.value) {
      shiftLinkedServices(move, move.startTime - previousStart)
    }

    const section = props.sections[active.move.section]
    section?.calculateCompression()
  }

  if (active?.resize) {
    const resize = active.resize
    const startTime = resize.startTime
    const duration = resize.duration + (distance / minuteSize.value)
    const finishTime = startTime + duration

    if (finishTime > 1440) {
      active.resize.duration = 1440 - startTime
    } else {
      active.resize.duration = duration
    }

    const section = props.sections[active.resize.section]
    section?.calculateCompression()
  }
}

function calcParentOffset() {
  const parent = document.getElementById('draggable')
  if (!parent) return { top: 0, left: 0 }
  const rect = parent.getBoundingClientRect()
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
  }
}

defineExpose({ scroll, active })
</script>
